import { Box } from "@mui/material";
import { CountyPicker } from "components/CountyPicker";
import { CountryPicker } from "components/Facility/CountryPicker";
import { FormTextField } from "components/Forms/FormTextField";
import { MunicipalityPicker } from "components/MunicipalityPicker";
import { StatePicker } from "components/StatePicker";
import { TIER_II_MANAGER_STATES_WITH_MUNICIPALITIES } from "encamp-shared/src/constants/t2m-county-lepc-fd/getByState";
import { useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";

export type AddressType = "physical" | "mailing";

type AddressFieldNames = {
  streetAddress1: string;
  streetAddress2: string;
  city: string;
  state: string;
  zip: string;
  county?: string;
  municipality?: string;
  country: string;
  latitude?: string;
  longitude?: string;
};

const fieldsByType: Record<AddressType, AddressFieldNames> = {
  physical: {
    streetAddress1: "streetAddress1",
    streetAddress2: "streetAddress2",
    city: "city",
    state: "state",
    zip: "zip",
    county: "county",
    municipality: "municipality",
    country: "country",
    latitude: "latitude",
    longitude: "longitude",
  },
  mailing: {
    streetAddress1: "mailingStreetAddress1",
    streetAddress2: "mailingStreetAddress2",
    city: "mailingCity",
    state: "mailingState",
    zip: "mailingZip",
    country: "mailingCountry",
  },
};

/**
 * Shared address field grid. Used for Physical, Mailing, etc.
 * Must be rendered inside a parent with CSS grid `gridTemplateColumns: repeat(12, 1fr)`.
 */
export function AddressGrid({ type, disabled }: { type: AddressType; disabled?: boolean }) {
  const fields = fieldsByType[type];
  const { control, watch, setValue } = useFormContext();
  const state = watch(fields.state) ?? "";
  const county = fields.county ? watch(fields.county) : undefined;
  const showMunicipalityPicker =
    Boolean(fields.municipality) &&
    Boolean(state && TIER_II_MANAGER_STATES_WITH_MUNICIPALITIES.includes(state));

  useEffect(() => {
    if (fields.municipality == null) {
      return;
    }
    if (state && !TIER_II_MANAGER_STATES_WITH_MUNICIPALITIES.includes(state)) {
      setValue(fields.municipality, null);
    }
  }, [fields.municipality, state, setValue]);

  let countryGridColumnSm = fields.county ? "span 6" : "span 12";
  if (fields.county && showMunicipalityPicker) {
    countryGridColumnSm = "span 12";
  }

  return (
    <>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <FormTextField
          name={fields.streetAddress1}
          label="Street Address"
          control={control}
          disabled={disabled}
        />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
        <FormTextField
          name={fields.streetAddress2}
          label="Street Address 2"
          control={control}
          disabled={disabled}
        />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 4" } }}>
        <FormTextField name={fields.city} label="City" control={control} disabled={disabled} />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 4" } }}>
        <StatePicker
          name={fields.state}
          countryFieldName={fields.country}
          control={control}
          disabled={disabled}
        />
      </Box>
      <Box sx={{ gridColumn: { xs: "span 12", sm: "span 4" } }}>
        <FormTextField name={fields.zip} label="Zip" control={control} disabled={disabled} />
      </Box>
      {fields.county && (
        <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
          <Controller
            name={fields.county}
            control={control}
            render={(renderProps) => (
              <CountyPicker
                {...(renderProps as Parameters<typeof CountyPicker>[0])}
                facilityState={state}
                disabled={disabled}
              />
            )}
          />
        </Box>
      )}
      {showMunicipalityPicker && fields.municipality && (
        <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
          <Controller
            name={fields.municipality}
            control={control}
            render={({ field, fieldState }) => (
              <MunicipalityPicker
                {...field}
                {...fieldState}
                disabled={disabled}
                facilityState={state}
                facilityCounty={county ?? undefined}
              />
            )}
          />
        </Box>
      )}
      <Box sx={{ gridColumn: { xs: "span 12", sm: countryGridColumnSm } }}>
        <CountryPicker name={fields.country} control={control} disabled={disabled} />
      </Box>
      {fields.latitude && fields.longitude && (
        <>
          <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
            <FormTextField
              name={fields.latitude}
              label="Latitude"
              control={control}
              disabled={disabled}
            />
          </Box>
          <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
            <FormTextField
              name={fields.longitude}
              label="Longitude"
              control={control}
              disabled={disabled}
            />
          </Box>
        </>
      )}
    </>
  );
}
